/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getAEMResource,
    getHomepage,
    getLanguageIso,
} from '../../selectors/generic';
import { isLoadingById } from '../../selectors/loader';
import { initializeProductPage } from '../../actions/productPage';
import ProductPricing from '../organisms/product-page/ProductPricing';
import { BundleSelector } from '../molecules/products/BundleSelector';
import MyPuratosArea from '../molecules/ecom/MyPuratosArea';
import HeaderPromoLabel from '../molecules/promotions/HeaderPromoLabel';
import VariationDetail from '../organisms/product-page/VariationDetail';
import {
    getProductPageError,
    getSkus,
    isProductPageLoaded,
} from '../../selectors/productPage';
import { IHomepage } from '../../interfaces/aem/IHomepage';
import { ProductAlert } from '../organisms/product-page/ProductAlert';
import ReactDOM from 'react-dom';
import ProductPackageSelector from '../molecules/products/ProductPackageSelector';
import { isLoggedIn } from '../../data/user.model';
import { trackGA4GuestViewItem } from '../../analytics/ga4/events/impression';

interface IProps {
    model: string;
}

export const productPackagingRoot = document.getElementById(
    'react-product--packaging'
);

export const ProductPage: FunctionComponent<IProps> = ({ model }) => {
    const dispatch = useDispatch();
    const homepage: IHomepage = useSelector(getHomepage);
    const resource = useSelector((state) => getAEMResource(state, model));
    const resourceLoading = useSelector((state) => isLoadingById(state, model));
    const skus = useSelector(getSkus);
    const productsLoaded = useSelector(isProductPageLoaded);
    const productPageError = useSelector(getProductPageError);

    useEffect(() => {
        if (resource?.productId && resource?.variationsResourceExists) {
            dispatch(initializeProductPage(resource.productId));
        }
        if (
            !isLoggedIn() &&
            resource?.variationsResourceExists &&
            resource?.variationsList
        ) {
            trackGA4GuestViewItem(
                resource?.variationsList,
                resource?.productTitle
            );
        }
    }, [resource]);

    const isObsolete: boolean = useMemo(() => {
        if (!homepage?.enableEcommerce) {
            return resource?.isObsolete ?? false;
        } else if (productsLoaded) {
            return (
                (skus?.findIndex((product) => !product.isObsolete) ?? 0) === -1
            );
        }
    }, [homepage, resource, skus]);

    if (resourceLoading || !resource) {
        return null;
    }

    const hasVariations = resource?.variationsResourceExists || false;
    const myPuratosProductAvailable =
        (resource?.productId && !productPageError && hasVariations) || false;

    return (
        <React.Fragment>
            <MyPuratosArea requireEcom ignoreRoleBased>
                <ProductAlert
                    error={hasVariations && productPageError}
                    resource={resource}
                    homepage={homepage}
                    isObsolete={hasVariations && isObsolete}
                />
            </MyPuratosArea>
            <VariationDetail variations={resource.variationsList} />

            {myPuratosProductAvailable &&
            resource?.variationsList?.length > 1 ? (
                <MyPuratosArea requireNonEcom>
                    {ReactDOM.createPortal(
                        <div className="product-page__packaging">
                            <ProductPackageSelector
                                variations={resource.variationsList}
                                obsolete={isObsolete}
                                restrictedView
                                noSplitView
                                condensed
                            />
                        </div>,
                        productPackagingRoot
                    )}
                </MyPuratosArea>
            ) : null}

            {myPuratosProductAvailable ? (
                <MyPuratosArea requireEcom ignoreRoleBased>
                    <HeaderPromoLabel productCategoryTag={resource.productCategoryTag} />
                    <ProductPricing
                        obsolete={isObsolete}
                        obsoleteText={homepage.obsoleteProduct}
                        variations={resource?.variationsList}
                        productCategoryTag={resource.productCategoryTag}
                    />
                    <BundleSelector />
                </MyPuratosArea>
            ) : null}
        </React.Fragment>
    );
};

export default ProductPage;
